import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  Component,
  Input,
  OnInit,
  ViewContainerRef,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { SnAbilityService } from '@lib/ability';
import { SnLocation, SnMedia } from '@lib/api/entities';
import { SnLocationsAPI } from '@lib/api/services';

import { LocationMediaDialogComponent } from '../location-media-dialog/location-media-dialog.component';

@Component({
  selector: 'sn-location-media',
  templateUrl: './location-media.container.html',
  styleUrls: ['./location-media.container.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LocationMediaContainer implements OnInit, OnDestroy {
  @Input()
  public location: SnLocation;

  public media: SnMedia[];
  public readonly canAdd: boolean;

  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _dialog: MatDialog,
    private readonly _viewContainerRef: ViewContainerRef,
    private readonly _locationsAPI: SnLocationsAPI,
    private readonly _cd: ChangeDetectorRef,
    ability: SnAbilityService,
  ) {
    this.canAdd = ability.can({ subject: 'maps/locationmedia', action: 'add' });
  }

  public ngOnInit(): void {
    this._fetchData();
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public add(): void {
    const dialogRef = this._dialog.open(LocationMediaDialogComponent, {
      data: this.location,
      viewContainerRef: this._viewContainerRef,
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        if (dialogRef.componentInstance.media) {
          this.media = dialogRef.componentInstance.media;
        }
        this._fetchData();
      });
  }

  public deleteItem(item: SnMedia): void {
    this._locationsAPI
      .deleteMedia(this.location.id, item.id)
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this._fetchData();
      });
  }

  private _fetchData(): void {
    this._locationsAPI
      .getLocation(this.location.id)
      .pipe(takeUntil(this._destroy$))
      .subscribe((location) => {
        this.media = location.media;
        this._cd.detectChanges();
      });
  }
}
